import { useQuery } from '@tanstack/react-query';

import type {
  RawDriverIdleEngine,
  RawDriverIdleEngineEvent,
  RawVehicleIdleEngine,
  RawVehicleIdleEngineEvent,
} from '@cobliteam/common-types';

import {
  type DriverIdleEngineEventsRequest,
  type IdleEngineAggregationsRequest,
  IdleEngineRequester,
  type VehicleIdleEngineEventsRequest,
} from '@cobliteam/api-lib';

import { Environment } from '@cobliteam/react-environments';

const env = Environment.getInstance().getEnvironment();
const idleEngineRequester = new IdleEngineRequester({ host: env.apiHost });

const VEHICLE_AGGREGATED_KEY = 'VEHICLE-AGGREGATED-IDLE-ENGINE';
const DRIVER_AGGREGATED_KEY = 'DRIVER-AGGREGATED-IDLE-ENGINE';
const VEHICLE_EVENTS_KEY = 'VEHICLE-EVENTS-IDLE-ENGINE';
const DRIVER_EVENTS_KEY = 'DRIVER-EVENTS-IDLE-ENGINE';

const useIdleEngine = () => {
  const getVehicleAggregatedIdleEngines = ({
    startDate,
    endDate,
    idleEngineThresholdInMinutes,
    enabled,
  }: IdleEngineAggregationsRequest & { enabled: boolean }) =>
    useQuery<RawVehicleIdleEngine[]>({
      queryKey: [
        VEHICLE_AGGREGATED_KEY,
        startDate,
        endDate,
        idleEngineThresholdInMinutes,
      ],
      queryFn: () =>
        idleEngineRequester.getVehicleIdleEngineAggregations(
          startDate,
          endDate,
          idleEngineThresholdInMinutes
        ),
      staleTime: 300000,
      enabled,
    });

  const getDriverAggregatedIdleEngines = ({
    startDate,
    endDate,
    idleEngineThresholdInMinutes,
    enabled,
  }: IdleEngineAggregationsRequest & { enabled: boolean }) =>
    useQuery<RawDriverIdleEngine[]>({
      queryKey: [
        DRIVER_AGGREGATED_KEY,
        startDate,
        endDate,
        idleEngineThresholdInMinutes,
      ],
      queryFn: () =>
        idleEngineRequester.getDriverIdleEngineAggregations(
          startDate,
          endDate,
          idleEngineThresholdInMinutes
        ),
      staleTime: 300000,
      enabled,
    });

  const getVehicleIdleEngineEvents = (
    {
      startDate,
      endDate,
      vehicleId,
      idleEngineThresholdInMinutes,
    }: VehicleIdleEngineEventsRequest,
    enabled: boolean
  ) =>
    useQuery<RawVehicleIdleEngineEvent[]>({
      queryKey: [
        VEHICLE_EVENTS_KEY,
        startDate,
        endDate,
        vehicleId,
        idleEngineThresholdInMinutes,
      ],
      queryFn: () =>
        idleEngineRequester.getVehicleIdleEngineEvents(
          startDate,
          endDate,
          vehicleId,
          idleEngineThresholdInMinutes
        ),
      staleTime: 300000,
      enabled,
    });

  const getDriverIdleEngineEvents = (
    {
      startDate,
      endDate,
      driverId,
      idleEngineThresholdInMinutes,
    }: DriverIdleEngineEventsRequest,
    enabled: boolean
  ) =>
    useQuery<RawDriverIdleEngineEvent[]>({
      queryKey: [
        DRIVER_EVENTS_KEY,
        startDate,
        endDate,
        driverId,
        idleEngineThresholdInMinutes,
      ],
      queryFn: () =>
        idleEngineRequester.getDriverIdleEngineEvents(
          startDate,
          endDate,
          driverId,
          idleEngineThresholdInMinutes
        ),
      staleTime: 300000,
      enabled,
    });

  return {
    getVehicleAggregatedIdleEngines,
    getDriverAggregatedIdleEngines,
    getVehicleIdleEngineEvents,
    getDriverIdleEngineEvents,
  };
};

export default useIdleEngine;
